<template>
  <b-card>
    <b-row>
      <b-col
        md="6"
        class="pb-1"
      >
        <b-button
          v-if="hasPermission('records-library', 'create')"
          variant="outline-primary"
          :to="{ name: 'organization-library-add' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('biblioteca.add_document') }}
        </b-button>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('message.search')"
            type="search"
            class="d-inline-block"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-row class="mb-1">
      <b-col md="3">
        <v-select
          v-model="tipoDocumentoFilter"
          :options="tipoDocumentoOptions"
          :reduce="opt => opt.value"
          label="title"
          :placeholder="$t('biblioteca.filter_tipo')"
          :clearable="true"
        />
      </b-col>
      <b-col md="3">
        <v-select
          v-model="estadoFilter"
          :options="estadoOptions"
          :reduce="opt => opt.value"
          label="title"
          :placeholder="$t('biblioteca.filter_estado')"
          :clearable="true"
        />
      </b-col>
      <b-col md="3">
        <b-form-input
          v-model="tematicaFilter"
          :placeholder="$t('biblioteca.filter_tematica')"
          type="search"
        />
      </b-col>
      <b-col
        md="3"
        class="d-flex align-items-center"
      >
        <b-form-checkbox v-model="showInactive">
          {{ $t('biblioteca.show_inactive') }}
        </b-form-checkbox>
      </b-col>
    </b-row>

    <!-- Table -->
    <b-row>
      <b-col cols="12">
        <b-table
          class="library-list-table"
          :items="documents"
          hover
          responsive
          :fields="tableColumns"
          primary-key="_id"
          :sort-by.sync="sortBy"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-desc.sync="isSortDirDesc"
        >

          <!-- Version -->
          <template #cell(versionActual)="data">
            <b-badge variant="light-info">
              v{{ data.item.versionActual || 1 }}
            </b-badge>
          </template>

          <!-- Estado -->
          <template #cell(estado)="data">
            <b-badge :variant="getEstadoVariant(data.item.estado)">
              {{ $t(`biblioteca.estado_${data.item.estado}`) }}
            </b-badge>
          </template>

          <!-- Fecha Registro -->
          <template #cell(fechaRegistro)="data">
            {{ data.item.fechaRegistroFormatted || '' }}
          </template>

          <!-- Fecha Proxima Revision -->
          <template #cell(fechaProximaRevision)="data">
            <span :class="{ 'text-warning': data.item.isProximoAVencer, 'text-danger font-weight-bold': data.item.isVencido }">
              {{ data.item.fechaProximaRevisionFormatted || '' }}
            </span>
            <b-badge
              v-if="data.item.isVencido"
              variant="danger"
              class="ml-50"
            >
              {{ $t('biblioteca.vencido') }}
            </b-badge>
            <b-badge
              v-else-if="data.item.isProximoAVencer"
              variant="warning"
              class="ml-50"
            >
              {{ $t('biblioteca.proximo_a_vencer') }}
            </b-badge>
          </template>

          <!-- Inactive badge -->
          <template #cell(nombre)="data">
            {{ data.item.nombre }}
            <b-badge
              v-if="!data.item.activo"
              variant="light-secondary"
              class="ml-50"
            >
              {{ $t('biblioteca.inactive') }}
            </b-badge>
          </template>

          <!-- Actions -->
          <template #cell(actions)="data">
            <div class="actions-grid">
              <ViewButton
                v-if="hasPermission('records-library', 'view')"
                @clicked="router.push({ name: 'organization-library-view', params: { id: data.item._id } })"
              />
              <EditButton
                v-if="hasPermission('records-library', 'edit')"
                @clicked="router.push({ name: 'organization-library-edit', params: { id: data.item._id } })"
              />
              <UploadButton
                v-if="data.item.activo && hasPermission('records-library', 'edit')"
                i18n-hover-variable="biblioteca.upload_new_version"
                @clicked="openUploadModal(data.item)"
              />
              <ValidateButton
                v-if="canValidateDocument(data.item)"
                i18n-hover-variable="biblioteca.validate"
                @clicked="openValidateModal(data.item)"
              />
              <RejectButton
                v-if="canValidateDocument(data.item)"
                i18n-hover-variable="biblioteca.reject"
                @clicked="openRejectModal(data.item)"
              />
              <SlashButton
                v-if="hasPermission('records-library', 'delete')"
                v-b-modal="`modal-deactivate-${data.item._id}`"
                variant="danger"
                i18n-hover-variable="biblioteca.deactivate"
              />
              <b-modal
                :id="`modal-deactivate-${data.item._id}`"
                centered
                no-close-on-backdrop
                modal-class="modal-danger"
                ok-variant="danger"
                cancel-variant="outline-secondary"
                :title="$t('message.confirm_action')"
                :ok-title="$t('biblioteca.deactivate')"
                :cancel-title="$t('message.cancel')"
                @ok="handleDeactivate(data.item._id)"
              >
                {{ $t('biblioteca.confirm_deactivate') }}
              </b-modal>
            </div>
          </template>
        </b-table>

        <!-- Upload new version modal -->
        <b-modal
          v-model="showUploadModal"
          :title="$t('biblioteca.upload_new_version')"
          centered
          no-close-on-backdrop
          ok-variant="info"
          :ok-title="$t('biblioteca.upload')"
          :cancel-title="$t('message.cancel')"
          :ok-disabled="!newVersionFile.name || isUploading"
          @ok="submitUploadNewVersion"
        >
          <p>{{ $t('biblioteca.upload_version_description') }}</p>
          <b-form-group :label="$t('label.document')">
            <b-button
              variant="info"
              @click="$refs.versionFileSelector.click()"
            >
              <feather-icon
                icon="UploadIcon"
                class="mr-50"
              />
              {{ $t('biblioteca.select_file') }}
            </b-button>
            <input
              ref="versionFileSelector"
              type="file"
              style="display: none"
              @change="onVersionFileSelected"
            >
            <p
              v-if="newVersionFile.name"
              class="mt-1"
            >
              <feather-icon
                icon="FileIcon"
                class="mr-25"
              />
              {{ newVersionFile.name }}
            </p>
          </b-form-group>
          <div v-if="isUploading">
            <loading
              :active="true"
              :is-full-page="false"
              color="#498ceb"
            />
            <p class="text-center">
              {{ $t('biblioteca.uploading') }}
            </p>
          </div>
        </b-modal>

        <!-- Validate modal -->
        <b-modal
          v-model="showValidateModal"
          :title="$t('biblioteca.validate_title')"
          centered
          no-close-on-backdrop
          ok-variant="success"
          :ok-title="$t('biblioteca.validate')"
          :cancel-title="$t('message.cancel')"
          :ok-disabled="isProcessing"
          @ok="submitValidate"
        >
          <p>{{ $t('biblioteca.validate_description') }}</p>
          <b-form-group :label="$t('biblioteca.fechaProximaRevision')">
            <b-form-datepicker
              v-model="validateFechaRevision"
              :locale="locale"
              :placeholder="$t('biblioteca.placeholder_fecha')"
            />
          </b-form-group>
          <div v-if="isProcessing">
            <loading
              :active="true"
              :is-full-page="false"
              color="#498ceb"
            />
          </div>
        </b-modal>

        <!-- Reject modal -->
        <b-modal
          v-model="showRejectModal"
          :title="$t('biblioteca.reject_title')"
          centered
          no-close-on-backdrop
          ok-variant="danger"
          :ok-title="$t('biblioteca.reject')"
          :cancel-title="$t('message.cancel')"
          :ok-disabled="!rejectMotivo.trim() || isProcessing"
          @ok="submitReject"
        >
          <p>{{ $t('biblioteca.reject_description') }}</p>
          <b-form-group :label="$t('biblioteca.rejection_reason')">
            <b-form-textarea
              v-model="rejectMotivo"
              :placeholder="$t('biblioteca.placeholder_rejection_reason')"
              rows="3"
              required
            />
          </b-form-group>
          <b-form-group :label="$t('biblioteca.fechaProximaRevision')">
            <b-form-datepicker
              v-model="rejectFechaRevision"
              :locale="locale"
              :placeholder="$t('biblioteca.placeholder_fecha')"
            />
          </b-form-group>
          <div v-if="isProcessing">
            <loading
              :active="true"
              :is-full-page="false"
              color="#498ceb"
            />
            <p class="text-center">
              {{ $t('message.loading') }}
            </p>
          </div>
        </b-modal>
      </b-col>

      <!-- Pagination -->
      <b-col
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0">
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="[5, 10, 15, 20, 25, 30, 40, 50, 100]"
              class="per-page-selector"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (documents.length ? 1 : 0)}`, to: `${documents.length + ((currentPage * perPage) - perPage)}`, total: `${totalDocuments}` }) }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalDocuments"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BBadge, BPagination, BTable, BModal, VBModal, BFormCheckbox, BFormInput, BFormDatepicker, BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, onMounted, computed } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import { useRouter } from '@core/utils/utils'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import SlashButton from '@/views/components/Shared/Buttons/SlashButton.vue'
import UploadButton from '@/views/components/Shared/Buttons/UploadButton.vue'
import ValidateButton from '@/views/components/Shared/Buttons/ValidateButton.vue'
import RejectButton from '@/views/components/Shared/Buttons/RejectButton.vue'
import i18n from '@/libs/i18n'
import { hasPermission } from '@/composables/usePermission'
import useLibraryList from './useLibraryList'

export default {
  components: {
    BTable,
    BBadge,
    BPagination,
    BModal,
    BFormCheckbox,
    BFormInput,
    BFormDatepicker,
    BFormTextarea,
    EditButton,
    ViewButton,
    SlashButton,
    UploadButton,
    ValidateButton,
    RejectButton,
    Loading,
    vSelect,
  },
  directives: {
    'b-modal': VBModal,
  },
  setup() {
    const { router } = useRouter()
    const {
      fetchDocuments,
      documents,
      tableColumns,
      perPage,
      currentPage,
      totalDocuments,
      searchQuery,
      sortBy,
      isSortDirDesc,
      isLoading,
      handleDeactivate,
      handleUploadNewVersion,
      handleValidate,
      handleReject,
      canValidateDocument,
      tipoDocumentoFilter,
      tematicaFilter,
      estadoFilter,
      showInactive,
      estadoOptions,
    } = useLibraryList()

    const { default_language: defaultLanguage } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const locale = defaultLanguage || 'es'

    // Un solo modal por acción (no uno por fila): selectedItem guarda la fila clickeada,
    // mismo patrón ya usado en ObligationManager.vue para modales a nivel de listado.
    const selectedItem = ref(null)

    const showUploadModal = ref(false)
    const newVersionFile = ref({})
    const isUploading = ref(false)

    const isProcessing = ref(false)
    const showValidateModal = ref(false)
    const showRejectModal = ref(false)
    const validateFechaRevision = ref('')
    const rejectMotivo = ref('')
    const rejectFechaRevision = ref('')

    const getDefaultFechaRevision = () => {
      const d = new Date()
      d.setMonth(d.getMonth() + 12)
      return d.toISOString().split('T')[0]
    }

    const openUploadModal = item => {
      selectedItem.value = item
      newVersionFile.value = {}
      showUploadModal.value = true
    }

    const onVersionFileSelected = e => {
      [newVersionFile.value] = e.target.files
    }

    const submitUploadNewVersion = async bvModalEvt => {
      bvModalEvt.preventDefault()
      if (!newVersionFile.value.name) return
      isUploading.value = true
      const success = await handleUploadNewVersion(selectedItem.value, newVersionFile.value)
      if (success) {
        showUploadModal.value = false
        newVersionFile.value = {}
      }
      isUploading.value = false
    }

    const openValidateModal = item => {
      selectedItem.value = item
      validateFechaRevision.value = getDefaultFechaRevision()
      showValidateModal.value = true
    }

    const submitValidate = async bvModalEvt => {
      bvModalEvt.preventDefault()
      isProcessing.value = true
      const fechaRevision = validateFechaRevision.value ? new Date(validateFechaRevision.value) : null
      const success = await handleValidate(selectedItem.value, fechaRevision)
      if (success) {
        showValidateModal.value = false
        validateFechaRevision.value = ''
      }
      isProcessing.value = false
    }

    const openRejectModal = item => {
      selectedItem.value = item
      rejectFechaRevision.value = getDefaultFechaRevision()
      rejectMotivo.value = ''
      showRejectModal.value = true
    }

    const submitReject = async bvModalEvt => {
      bvModalEvt.preventDefault()
      if (!rejectMotivo.value.trim()) return
      isProcessing.value = true
      const fechaRevision = rejectFechaRevision.value ? new Date(rejectFechaRevision.value) : null
      const success = await handleReject(selectedItem.value, rejectMotivo.value.trim(), fechaRevision)
      if (success) {
        showRejectModal.value = false
        rejectMotivo.value = ''
        rejectFechaRevision.value = ''
      }
      isProcessing.value = false
    }

    const tipoDocumentoOptions = computed(() => [
      { value: 'Política', title: i18n.t('biblioteca.tipo_politica') },
      { value: 'Procedimiento', title: i18n.t('biblioteca.tipo_procedimiento') },
      { value: 'Manual', title: i18n.t('biblioteca.tipo_manual') },
      { value: 'Instructivo', title: i18n.t('biblioteca.tipo_instructivo') },
      { value: 'Formato', title: i18n.t('biblioteca.tipo_formato') },
      { value: 'Otro', title: i18n.t('biblioteca.tipo_otro') },
    ])

    const getEstadoVariant = estado => {
      const variants = {
        pendiente: 'light-warning',
        validado: 'light-success',
        rechazado: 'light-danger',
        vencido: 'light-secondary',
      }
      return variants[estado] || 'light-info'
    }

    onMounted(() => {
      fetchDocuments()
    })

    return {
      router,
      fetchDocuments,
      documents,
      tableColumns,
      perPage,
      currentPage,
      totalDocuments,
      searchQuery,
      sortBy,
      isSortDirDesc,
      isLoading,
      handleDeactivate,
      canValidateDocument,
      tipoDocumentoFilter,
      tematicaFilter,
      estadoFilter,
      showInactive,
      estadoOptions,
      tipoDocumentoOptions,
      getEstadoVariant,
      hasPermission,
      locale,
      selectedItem,
      showUploadModal,
      newVersionFile,
      isUploading,
      isProcessing,
      showValidateModal,
      showRejectModal,
      validateFechaRevision,
      rejectMotivo,
      rejectFechaRevision,
      openUploadModal,
      onVersionFileSelected,
      submitUploadNewVersion,
      openValidateModal,
      submitValidate,
      openRejectModal,
      submitReject,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.actions-grid {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.1rem;

  .btn-icon {
    padding: 0.4rem;
  }
}

.library-list-table {
  ::v-deep .library-col-actions {
    width: 1%;
    white-space: nowrap;
    padding-left: 0rem !important;
    padding-right: 0rem !important;
  }

  ::v-deep th.library-col-actions {
    text-align: center;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
